import { lazy, ReactNode } from "react";
import { AdminRole } from "store/slices/authSlice";

// Lazy loading: chaque layout part dans son propre chunk JS, chargé
// uniquement quand la route est visitée (au lieu d'un seul gros bundle).
const Dashboard = lazy(() => import("layouts/dashboard"));
const Tables = lazy(() => import("layouts/tables"));
const Billing = lazy(() => import("layouts/billing"));
const Profile = lazy(() => import("layouts/profile"));
const Users = lazy(() => import("layouts/users"));
const Plans = lazy(() => import("layouts/plans"));
const Payments = lazy(() => import("layouts/payments"));
const Tenants = lazy(() => import("layouts/tenants"));
const PromoCodes = lazy(() => import("layouts/promo-codes"));
const Emails = lazy(() => import("layouts/emails"));
const Tickets = lazy(() => import("layouts/tickets"));
const Referrals = lazy(() => import("layouts/referrals"));
const Wallets = lazy(() => import("layouts/wallets"));
const Settings = lazy(() => import("layouts/settings"));
const Queues = lazy(() => import("layouts/queues"));
const Webhooks = lazy(() => import("layouts/webhooks"));
const SignIn = lazy(() => import("layouts/authentication/sign-in"));

// Soft UI Dashboard React icons
import Shop from "components/Icons/Shop";
import Office from "components/Icons/Office";
import Document from "components/Icons/Document";
import CustomerSupport from "components/Icons/CustomerSupport";
import CreditCard from "components/Icons/CreditCard";

interface Route {
  type: "collapse" | "title" | "divider";
  name?: string;
  key: string;
  route?: string;
  href?: string;
  icon?: ReactNode;
  component?: ReactNode;
  noCollapse?: boolean;
  title?: string;
  roles?: AdminRole[];
  collapse?: Route[];
}

const routes: Route[] = [
  { type: "title", title: "Tableau de bord", key: "section-dashboard" },
  {
    type: "collapse",
    name: "Tableau de bord",
    key: "dashboard",
    route: "/dashboard",
    icon: <Shop size="18px" />,
    component: <Dashboard />,
    roles: ["ADMIN", "SUPER_ADMIN"],
    noCollapse: true,
  },
  { type: "divider", key: "div-1" },

  { type: "title", title: "Utilisateurs", key: "section-users" },
  {
    type: "collapse",
    name: "Utilisateurs",
    key: "users",
    route: "/users",
    icon: <CustomerSupport size="18px" />,
    component: <Users />,
    roles: ["SUPER_ADMIN"],
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Plans",
    key: "plans",
    route: "/plans",
    icon: <CreditCard size="18px" />,
    component: <Plans />,
    roles: ["SUPER_ADMIN"],
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Paiements",
    key: "payments",
    route: "/payments",
    icon: <CreditCard size="18px" />,
    component: <Payments />,
    roles: ["SUPER_ADMIN"],
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Codes Promo",
    key: "promo-codes",
    route: "/promo-codes",
    icon: <CreditCard size="18px" />,
    component: <PromoCodes />,
    roles: ["SUPER_ADMIN"],
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Parrainage",
    key: "referrals",
    route: "/referrals",
    icon: <CreditCard size="18px" />,
    component: <Referrals />,
    roles: ["ADMIN", "SUPER_ADMIN"],
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Wallets",
    key: "wallets",
    route: "/wallets",
    icon: <CreditCard size="18px" />,
    component: <Wallets />,
    roles: ["ADMIN", "SUPER_ADMIN"],
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Emails",
    key: "emails",
    route: "/emails",
    icon: <Document size="18px" />,
    component: <Emails />,
    roles: ["SUPER_ADMIN"],
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Tickets Support",
    key: "tickets",
    route: "/tickets",
    icon: <CustomerSupport size="18px" />,
    component: <Tickets />,
    roles: ["ADMIN", "SUPER_ADMIN"],
    noCollapse: true,
  },
  { type: "divider", key: "div-2" },

  { type: "title", title: "Zone Admin", key: "zone-admin" },
  {
    type: "collapse",
    name: "Workspaces",
    key: "tenants",
    route: "/tenants",
    icon: <Office size="18px" />,
    component: <Tenants />,
    roles: ["SUPER_ADMIN"],
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Administrateurs",
    key: "admins",
    route: "/admins",
    icon: <Office size="18px" />,
    component: <Tables />,
    roles: ["SUPER_ADMIN"],
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Logs d'audit",
    key: "audit-logs",
    route: "/logs",
    icon: <CreditCard size="18px" />,
    component: <Billing />,
    roles: ["SUPER_ADMIN"],
    noCollapse: true,
  },
  { type: "divider", key: "div-3" },

  { type: "title", title: "Système", key: "section-system" },
  {
    type: "collapse",
    name: "Configuration",
    key: "settings",
    route: "/settings",
    icon: <Document size="18px" />,
    component: <Settings />,
    roles: ["SUPER_ADMIN"],
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Queues",
    key: "queues",
    route: "/queues",
    icon: <Document size="18px" />,
    component: <Queues />,
    roles: ["SUPER_ADMIN"],
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Webhooks",
    key: "webhooks",
    route: "/webhooks",
    icon: <Document size="18px" />,
    component: <Webhooks />,
    roles: ["SUPER_ADMIN"],
    noCollapse: true,
  },
  { type: "divider", key: "div-4" },

  { type: "title", title: "Compte", key: "account-pages" },
  {
    type: "collapse",
    name: "Profil",
    key: "profile",
    route: "/profile",
    icon: <CustomerSupport size="18px" />,
    component: <Profile />,
    roles: ["ADMIN", "SUPER_ADMIN"],
    noCollapse: true,
  },
  {
    type: "collapse",
    name: "Sign In",
    key: "sign-in",
    route: "/authentication/sign-in",
    icon: <Document size="18px" />,
    component: <SignIn />,
    noCollapse: true,
  },
];

export default routes;
